const RESULT_EVENT = "suarte:native-google-sign-in-result";
const REQUEST_TIMEOUT_MS = 2 * 60 * 1000;

function createAuthError(code, message, cause) {
  const error = new Error(message);
  error.code = code;
  if (cause) error.cause = cause;
  return error;
}

function getNativeHandler() {
  if (typeof window === "undefined") {
    return null;
  }

  return window.webkit?.messageHandlers?.SuarteGoogleSignIn ?? null;
}

function createRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `google-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function isNativeGoogleSignInAvailable() {
  return typeof getNativeHandler()?.postMessage === "function";
}

export function requestNativeGoogleCredentials() {
  const handler = getNativeHandler();
  if (typeof handler?.postMessage !== "function") {
    return Promise.reject(createAuthError(
      "auth/native-google-unavailable",
      "Native Google sign-in is unavailable.",
    ));
  }

  const requestId = createRequestId();

  return new Promise((resolve, reject) => {
    let timeoutId = null;

    const cleanUp = () => {
      window.removeEventListener(RESULT_EVENT, handleResult);
      if (timeoutId) window.clearTimeout(timeoutId);
    };

    const handleResult = (event) => {
      const result = event?.detail;
      if (!result || result.requestId !== requestId) {
        return;
      }

      cleanUp();
      const idToken = typeof result.idToken === "string" && result.idToken
        ? result.idToken
        : null;
      const accessToken = typeof result.accessToken === "string" && result.accessToken
        ? result.accessToken
        : null;

      if (result.ok && (idToken || accessToken)) {
        resolve({
          idToken,
          accessToken,
        });
        return;
      }

      reject(createAuthError(
        result.code || "auth/native-google-failed",
        result.message || "Google sign-in did not complete.",
      ));
    };

    window.addEventListener(RESULT_EVENT, handleResult);
    timeoutId = window.setTimeout(() => {
      cleanUp();
      reject(createAuthError(
        "auth/native-google-timeout",
        "Google sign-in timed out.",
      ));
    }, REQUEST_TIMEOUT_MS);

    try {
      handler.postMessage({ requestId });
    } catch (error) {
      cleanUp();
      reject(createAuthError(
        "auth/native-google-bridge-error",
        "Could not start native Google sign-in.",
        error,
      ));
    }
  });
}

export const NATIVE_GOOGLE_RESULT_EVENT = RESULT_EVENT;
